import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import TrackMap from '../components/tracks/TrackMap';
import CustomGeocoder from '../components/tracks/CustomGeocoder';
import { fetchOSMData, filterOSMData } from '../components/tracks/Overpass';
import { generateGeoJSON } from '../components/tracks/TrackProcessing.js';
import './BoardTracks.css';
import { Panel, PanelGroup } from 'react-resizable-panels';
import styles from './styles.module.css';
import ResizeHandle from './ResizeHandle';
import DatabaseService from '../services/database.service.js';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import TrackGrid from './tracks/TrackGrid.js';
import TrackValidationModal from './tracks/TrackValidationModal';

const BoardTracks = () => {
  const [geojson, setGeojson] = useState(null);
  // const [location, setLocation] = useState({ lat: 40, lng: -100 });
  const [circuitList, setCircuitList] = useState([]);
  const [radius, setRadius] = useState('');
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [resetSelectedCircuit, setResetSelectedCircuit] = useState(0);

  const [circuitGeojson, setCircuitGeojson] = useState(null);
  const [finishLineGeojson, setFinishLineGeojson] = useState(null);
  const [confirmzoneGeojson, setConfirmzoneGeojson] = useState(null);
  const [circuitName, setCircuitName] = useState('');
  const [countryCode, setCountryCode] = useState('FR'); // Default to France
  const [applySmoothing, setApplySmoothing] = useState(false);

  const mapRef = useRef(null);
  const isDesktop = !window.mobileCheck();

  const parseCompleteGeojson = useCallback((geojson) => {
    if (geojson) {
      let circuit = null;
      let finishLine = null;
      let confirmZone = null;

      Object.values(geojson).forEach((feature) => {
        if (feature.geometry.type === 'LineString' && feature.geometry.coordinates.length > 20) {
          circuit = feature;
        } else if (feature.geometry.type === 'LineString' && feature.geometry.coordinates.length === 2) {
          finishLine = feature;
        } else if (feature.geometry.type === 'Polygon') {
          confirmZone = feature;
        }
      });

      if (circuit !== circuitGeojson) setCircuitGeojson(circuit);
      if (finishLine !== finishLineGeojson) setFinishLineGeojson(finishLine);
      if (confirmZone !== confirmzoneGeojson) setConfirmzoneGeojson(confirmZone);

      console.log("circuit", circuit);
      console.log("finishLine", finishLine);
      console.log("confirmZone", confirmZone);

    } else {
      setCircuitGeojson(null);
      setFinishLineGeojson(null);
      setConfirmzoneGeojson(null);
    }
  }, [circuitGeojson, finishLineGeojson, confirmzoneGeojson]);

  const handleMapClick = useCallback(async (lngLat) => {
    const { lng, lat } = lngLat;
    console.log("Map Clicked at:", lat, lng);

    const searchToasId = toast.loading('Processing data...', {
      position: "bottom-right",
      autoClose: false
    });

    const osmData = await fetchOSMData(lat, lng, Number(radius) ? Number(radius) / 1000 : 5);

    if (osmData) {
      const filterTags = { highway: "raceway" };
      const filteredData = filterOSMData(osmData, filterTags);

      const nodes = {};
      osmData.elements.forEach((element) => {
        if (element.type === 'node') {
          nodes[element.id] = element;
        }
      });

      const geojsonData = generateGeoJSON(filteredData, nodes, applySmoothing);

      if (geojsonData.features.length > 0) {
        setGeojson(geojsonData);
        toast.dismiss(searchToasId);
        console.log("Processed GeoJSON:", geojsonData);
      } else {
        toast.dismiss(searchToasId);
        toast.warn('No circuit track found', {
          position: "bottom-right",
        });
        console.warn("No valid features found in the Overpass data.");
      }
    } else {
      toast.dismiss(searchToasId);
    }
  }, [radius, applySmoothing]);

  const updateCircuitStatus = useCallback((id, status) => {
    DatabaseService.editCircuitStatus(id, status).then((response) => {
      if ((response.status === 200) || (response.status === 204)) {
        toast.success('Circuit status updated successfully', {
          position: "bottom-right"
        });
        loadCircuits();
      } else {
        toast.error('Failed to update circuit status', {
          position: "bottom-right"
        });
        loadCircuits();
      }
    })
  })

  

  const loadData = useCallback((id) => {
    const circuit = circuitList.find((circuit) => circuit.id === id);

    if (circuit) {
      const circuitFeatures = circuit.circuit_geojson.features
        ? circuit.circuit_geojson.features
        : [circuit.circuit_geojson];

      const finishLineFeatures = circuit.finish_geojson.features
        ? circuit.finish_geojson.features
        : [circuit.finish_geojson];

      const confirmZoneFeatures = circuit.confirm_geojson.features
        ? circuit.confirm_geojson.features
        : [circuit.confirm_geojson];

      setCircuitGeojson(circuit.circuit_geojson);
      setFinishLineGeojson(circuit.finish_geojson);
      setConfirmzoneGeojson(circuit.confirm_geojson);
      setCircuitName(circuit.name || '');
      setCountryCode(circuit.country_code || 'FR');
      setEditMode(true);

      setGeojson({
        type: "FeatureCollection",
        features: [
          ...circuitFeatures,
          ...finishLineFeatures,
          ...confirmZoneFeatures,
        ]
      });
    }
  }, [circuitList]);

  const memoizedGeojson = useMemo(() => geojson, [geojson]);

  const handleSubmitTrack = useCallback(() => {
    setShowValidationModal(true);
  }, []);

  const handleSelectLocation = useCallback((geometry) => {
    let coordinates;

    switch (geometry.type) {
      case "Point":
        coordinates = geometry.coordinates;
        break;
      case "Polygon":
        coordinates = geometry.coordinates[0][0];
        break;
      case "MultiPolygon":
        coordinates = geometry.coordinates[0][0][0];
        break;
      case "LineString":
        coordinates = geometry.coordinates[0];
        break;
      default:
        console.error("Unhandled geometry type:", geometry.type);
        return;
    }

    // setLocation({ lat: coordinates[1], lng: coordinates[0] });

    if (mapRef.current) {
      const map = mapRef.current.getMap();
      map.flyTo({ center: [coordinates[0], coordinates[1]], zoom: 16 });
    }
  }, []);

  const loadCircuits = () => {

    DatabaseService.getCircuits(true).then((response) => {
      setCircuitList(response.data.filter((circuit) => circuit.id !== 0));
    });
  }


  const handleSaveTrack = useCallback((trackData) => {

    // Use circuitName or another identifier if circuit isn't directly available
    const circuitId = circuitList.find(c => c.name === circuitName)?.id;

    if (!editMode || circuitId === undefined) {
      const payload = {
        name: trackData.trackName,
        country_code: trackData.countryCode,
        circuit_geojson: trackData.circuitGeojson,
        finish_geojson: trackData.finishLineGeojson,
        confirm_geojson: trackData.confirmzoneGeojson,
        status: 0
      };

      DatabaseService.createCircuit(payload)
        .then(() => {
          toast.success('Track saved successfully', {
            position: "bottom-right",
          });
          setShowValidationModal(false);

          loadCircuits();
        })
        .catch(() => {
          toast.error('Failed to save track', {
            position: "bottom-right",
          });
        });
    } else {
      const payload = {
        name: trackData.trackName,
        country_code: trackData.countryCode,
        circuit_geojson: trackData.circuitGeojson,
        finish_geojson: trackData.finishLineGeojson,
        confirm_geojson: trackData.confirmzoneGeojson,
      };


      if (!circuitId) {
        toast.error('Circuit ID not found. Unable to update track.');
        return;
      }

      DatabaseService.editCircuit(circuitId, payload) // Pass the correct circuit ID
        .then(() => {
          toast.success('Track edited successfully', {
            position: "bottom-right",
          });
          setShowValidationModal(false);


          loadCircuits();
        })
        .catch(() => {
          toast.error('Failed to edit track', {
            position: "bottom-right",
          });
        });
    }

  }, [editMode, circuitName, countryCode, circuitList]);


  useEffect(() => {
    loadCircuits();
  }, []);

  return (
    <>
      <ToastContainer />
      <div className={styles.Container} style={{ height: '92vh', flex: '1' }}>
        <PanelGroup direction="horizontal">
          <Panel collapsible={true} defaultSize={15} order={1}>
            <PanelGroup direction="vertical">
              <Panel className={styles.Panel} collapsible={true}>
                <TrackGrid
                  circuitList={circuitList}
                  isDesktop={isDesktop}
                  loadData={loadData}
                  updateCircuitStatus={updateCircuitStatus}
                  resetSelectedCircuit={resetSelectedCircuit}
                />
              </Panel>
            </PanelGroup>
          </Panel>
          <ResizeHandle iconName={'fa fa-left-right'} />
          <Panel collapsible={false} order={2}>
            <PanelGroup direction="vertical">
              <Panel
                className={styles.Panel}
                collapsible={true}
                defaultSize={85}
                minSize={40}
                order={1}
              >
                <div className="parent-container">
                  <div className="geocoder-button-container">
                    <CustomGeocoder
                      mapRef={mapRef}
                      onSelectLocation={handleSelectLocation}
                    />
                    <div className="button-group" style={{ display: 'flex', alignItems: 'center' }}>
                      <button
                        className="btn btn-primary"
                        onClick={() => {
                          setGeojson(null);
                          setEditMode(false);
                          setResetSelectedCircuit(resetSelectedCircuit + 1);
                        }}
                        style={{ marginRight: '10px' }}
                      >
                        Clear
                      </button>
                      <input
                        type="text"
                        value={radius}
                        onChange={(e) => setRadius(e.target.value)}
                        placeholder="Search radius (meters)"
                        className="track-input"
                        style={{ marginRight: '10px', flex: '1' }}
                      />
                      <button
                        className={`btn ${applySmoothing ? "btn-primary" : "btn-secondary"}`}
                        onClick={() => {
                          setApplySmoothing(!applySmoothing)
                          setGeojson(null);
                          setEditMode(false);
                          setResetSelectedCircuit(resetSelectedCircuit + 1);
                        }
                        }
                        style={{ marginRight: '10px' }}
                      >
                        Smoothing
                      </button>
                      <button
                        className="btn btn-primary"
                        onClick={handleSubmitTrack}
                        style={{ marginRight: '20px' }}
                      >
                        Submit
                      </button>

                    </div>
                  </div>
                  <div id="map-container" style={{ flex: 1 }}>
                    <TrackMap
                      mapRef={mapRef}
                      handleMapClick={handleMapClick}
                      parseCompleteGeojson={parseCompleteGeojson}
                      geojson={memoizedGeojson}
                      radius={Number(radius) ? Number(radius / 1000) : 5}
                    />
                  </div>
                </div>
              </Panel>
            </PanelGroup>
          </Panel>
        </PanelGroup>
      </div>

      <TrackValidationModal
        show={showValidationModal}
        onHide={() => setShowValidationModal(false)}
        circuitGeojson={circuitGeojson}
        finishLineGeojson={finishLineGeojson}
        confirmzoneGeojson={confirmzoneGeojson}
        circuitNameInit={circuitName}
        setCircuitName={setCircuitName}
        countryCodeInit={countryCode}
        onSave={handleSaveTrack}
      />
    </>
  );
}

export default BoardTracks;